import { useState, useEffect, useRef } from "react";
import axiosInstance from "../../../services/axios-instance";
import { NATIONAL_HOLIDAYS_URL } from "../../../services/endpoints";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Grid,
} from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";

interface PublicHoliday {
    id: number;
    title: string;
    date: string;
}

const validationSchema = Yup.object({
    title: Yup.string().required("Holiday Title is required"),
    date: Yup.string()
        .required("Holiday Date is required")
        .matches(/^\d{4}-\d{2}-\d{2}$/, "Date must be in YYYY-MM-DD format"),
});

const AdminNationalHolidaysScreen = () => {
    const [publicHolidays, setPublicHolidays] = useState<PublicHoliday[]>([]);
    const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
    const [holidayToDelete, setHolidayToDelete] = useState<number | null>(null);
    const [editingHolidayId, setEditingHolidayId] = useState<number | null>(
        null
    );

    const formRef = useRef<any>(null);

    useEffect(() => {
        fetchPublicHolidays();
    }, []);

    const fetchPublicHolidays = async () => {
        try {
            const response = await axiosInstance.get(NATIONAL_HOLIDAYS_URL);
            const data = response.data.data;

            // Sort the holidays by date
            const sortedData = data.sort(
                (a: PublicHoliday, b: PublicHoliday) => {
                    return a.date.localeCompare(b.date);
                }
            );

            setPublicHolidays(sortedData);
        } catch (error) {
            console.error("Error fetching public holidays:", error);
        }
    };

    const handleEditClick = (holidayId: number) => {
        setEditingHolidayId(holidayId);
    };

    const handleSaveClick = async (values: PublicHoliday) => {
        try {
            const response = await axiosInstance.patch(
                `${NATIONAL_HOLIDAYS_URL}/${values.id}`,
                {
                    title: values.title,
                    dates: [values.date],
                }
            );
            if (response.status === 200) {
                const updatedHolidays = publicHolidays.map((holiday) =>
                    holiday.id === values.id ? values : holiday
                );
                setPublicHolidays(updatedHolidays);
                setEditingHolidayId(null);
                fetchPublicHolidays();
            } else {
                console.error("Error updating holiday");
            }
        } catch (error) {
            console.error("Error updating holiday:", error);
        }
    };

    const handleDeleteClick = (holidayId: number) => {
        setHolidayToDelete(holidayId);
        setDeleteConfirmationOpen(true);
    };

    const confirmDeletePublicHoliday = async () => {
        try {
            await axiosInstance.delete(
                `${NATIONAL_HOLIDAYS_URL}/${holidayToDelete}`
            );
            const updatedHolidays = publicHolidays.filter(
                (holiday) => holiday.id !== holidayToDelete
            );
            setPublicHolidays(updatedHolidays);
        } catch (error) {
            console.error("Error deleting public holiday:", error);
        }
        setDeleteConfirmationOpen(false);
    };

    const cancelDeletePublicHoliday = () => {
        setHolidayToDelete(null);
        setDeleteConfirmationOpen(false);
    };

    return (
        <div>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell>Edit</TableCell>
                            <TableCell>Delete</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {publicHolidays.map((holiday) => (
                            <TableRow
                                key={holiday.id}
                                sx={{ backgroundColor: "white" }}
                            >
                                <TableCell colSpan={2}>
                                    {editingHolidayId === holiday.id ? (
                                        <Formik
                                            initialValues={{ ...holiday }}
                                            validationSchema={validationSchema}
                                            onSubmit={handleSaveClick}
                                            innerRef={formRef}
                                        >
                                            {({
                                                handleSubmit,
                                                handleChange,
                                                errors,
                                                touched,
                                            }) => (
                                                <Form onSubmit={handleSubmit}>
                                                    <Grid container spacing={2}>
                                                        <Grid
                                                            item
                                                            xs={12}
                                                            md={6}
                                                        >
                                                            <Field
                                                                as={TextField}
                                                                name="title"
                                                                label="Title"
                                                                error={
                                                                    touched.title &&
                                                                    !!errors.title
                                                                }
                                                                helperText={
                                                                    touched.title &&
                                                                    errors.title
                                                                }
                                                                onChange={
                                                                    handleChange
                                                                }
                                                            />
                                                        </Grid>
                                                        <Grid
                                                            item
                                                            xs={12}
                                                            md={6}
                                                        >
                                                            <Field
                                                                as={TextField}
                                                                name="date"
                                                                label="Date"
                                                                type="date"
                                                                InputLabelProps={{
                                                                    shrink: true,
                                                                }}
                                                                error={
                                                                    touched.date &&
                                                                    !!errors.date
                                                                }
                                                                helperText={
                                                                    touched.date &&
                                                                    errors.date
                                                                }
                                                                onChange={
                                                                    handleChange
                                                                }
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                    <Button
                                                        style={{
                                                            display: "none",
                                                        }}
                                                        type="submit"
                                                        variant="contained"
                                                        color="primary"
                                                        onClick={() =>
                                                            formRef.current.submitForm()
                                                        }
                                                    >
                                                        Save
                                                    </Button>
                                                </Form>
                                            )}
                                        </Formik>
                                    ) : (
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={6}>
                                                {holiday.title}
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                {holiday.date}
                                            </Grid>
                                        </Grid>
                                    )}
                                </TableCell>
                                <TableCell>
                                    {editingHolidayId === holiday.id ? (
                                        <Button
                                            variant="contained"
                                            color="secondary"
                                            onClick={() =>
                                                formRef.current.submitForm()
                                            }
                                        >
                                            Save
                                        </Button>
                                    ) : (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={() =>
                                                handleEditClick(holiday.id)
                                            }
                                        >
                                            Edit
                                        </Button>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <Button
                                        variant="contained"
                                        color="error"
                                        onClick={() =>
                                            handleDeleteClick(holiday.id)
                                        }
                                    >
                                        Delete
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog
                open={deleteConfirmationOpen}
                onClose={cancelDeletePublicHoliday}
            >
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    Are you sure you want to delete this public holiday?
                </DialogContent>
                <DialogActions>
                    <Button onClick={cancelDeletePublicHoliday}>Cancel</Button>
                    <Button onClick={confirmDeletePublicHoliday} autoFocus>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default AdminNationalHolidaysScreen;
